import type { useTranslations } from 'next-intl';

import { choisirLaSuitePourPanierRetenu } from './choixPanierRetenu';
import type { AvailabilityPartition } from './partitionAvailability';

type Translate = ReturnType<typeof useTranslations>;

/**
 * Ce que la modale de refus affiche. Tout sauf `onConfirm` : le geste de
 * retrait appartient au hook, qui seul tient le panier.
 */
export interface AvailabilityRefusalModal {
  title: string;
  message: string;
  showCancel: boolean;
  confirmLabel: string;
  cancelLabel: string;
  /** Vrai quand le bouton de confirmation retire les pièces retenues. */
  offerRemoval: boolean;
}

// ═══ UNE SEULE MODALE, PARCE QU'IL N'Y EN A QU'UNE ═══════════
//
// ⚠️ `infoModal` EST UNE INSTANCE UNIQUE (`useModal(InfoModal)`), et
// chaque `show()` ÉCRASE les arguments du précédent. Le code appelait
// `show()` jusqu'à trois fois de suite — une par œuvre vendue, une
// pour le lot retenu, une pour les lignes invérifiables : seule la
// DERNIÈRE s'affichait (revue du 2026-09-09).
//
// Tant qu'il ne s'agissait que de textes, le défaut coûtait une
// information. Depuis que la modale du lot retenu porte une ACTION —
// « Retirer ces œuvres et continuer » —, il coûte l'issue elle-même :
// sur un panier mêlant une pièce retenue et une ligne invérifiable,
// le client ne voyait jamais le bouton, et la caisse restait bloquée
// comme avant le correctif.
//
// On compose donc UN message qui porte tous les faits constatés, et
// les boutons de celui qui appelle une décision.
//
// Rend `null` quand aucun fait n'est constaté : le panier passe.
export function buildAvailabilityRefusalModal(
  {
    unverifiedLines,
    judgedLines,
    unavailable,
    heldByOthers,
  }: AvailabilityPartition,
  t: Translate,
): AvailabilityRefusalModal | null {
  const faits: string[] = [];

  // 1. LES PIÈCES PARTIES. Le hook les retire (seul retrait automatique du
  //    chantier, justifié : le catalogue fait foi) ; ici, leur seul message.
  unavailable.forEach((item) => {
    faits.push(
      t
        .rich('Blocs.ModalLivreIndisponible.message', {
          titre_principal: item.titre_principal,
        })
        ?.toString() ?? '',
    );
  });

  // 2. LES PIÈCES TENUES PAR UN TIERS. Le seul fait qui appelle une
  //    décision du client, donc le seul qui commande les boutons.
  //
  // ⚠️ LE DÉNOMINATEUR COMPTE CE QUI RESTE COMMANDABLE, et il a d'abord
  // compté trop large : les œuvres indisponibles sont RETIRÉES
  // par le hook, les compter ici promettait un « reste » qui
  // n'existait plus. Panier de deux pièces, une vendue et une retenue :
  // le client acceptait « retirer et continuer » et arrivait en caisse
  // avec un panier VIDE.
  const choix = choisirLaSuitePourPanierRetenu(
    heldByOthers.length,
    judgedLines.length - unavailable.length + unverifiedLines.length,
  );

  if (heldByOthers.length > 0) {
    const titresRetenus = heldByOthers
      // join(', ') plus un compteur séparé : le formateur ICU `list`
      // n'est employé nulle part dans le dépôt, l'introduire ici serait
      // une nouveauté non éprouvée par la configuration next-intl.
      .map((item) => item.titre_principal)
      .join(', ');

    faits.push(
      t(
        choix.proposerLeRetrait
          ? 'Blocs.ModalOeuvreRetenue.message'
          : // Tout le panier est retenu : « commander le reste » ne
            // désigne rien, la modale redevient un constat.
            'Blocs.ModalOeuvreRetenue.message_panier_entier',
        { nb: choix.nombreRetenu, titres: titresRetenus },
      ),
    );
  }

  // 3. LES LIGNES QU'ON N'A PAS SU JUGER. Un fait unique, qui ne se
  //    compte pas par titre, et qui ne retire rien.
  if (unverifiedLines.length > 0) {
    faits.push(t('Blocs.ModalControleImpossible.message'));
  }

  if (faits.length === 0) return null;

  // Le titre suit le fait qui appelle une décision quand il y en a
  // un ; sinon celui de la première cause constatée.
  let cleDuTitre = 'Blocs.ModalControleImpossible.titre';
  if (heldByOthers.length > 0) {
    cleDuTitre = 'Blocs.ModalOeuvreRetenue.titre';
  } else if (unavailable.length > 0) {
    cleDuTitre = 'Blocs.ModalLivreIndisponible.titre';
  }

  const offerRemoval = heldByOthers.length > 0 && choix.proposerLeRetrait;

  return {
    title: t(cleDuTitre),
    message: faits.join('\n\n'),
    showCancel: offerRemoval,
    confirmLabel: offerRemoval
      ? t('Blocs.ModalOeuvreRetenue.bouton_retirer', {
          nb: choix.nombreRetenu,
        })
      : t('Blocs.ModalOeuvreRetenue.bouton'),
    cancelLabel: t('Blocs.ModalOeuvreRetenue.bouton_attendre'),
    offerRemoval,
  };
}
